import Vue from 'vue'
import { apiSecond } from '@/boot/axios'

// Непросмотренные обновления системы: один запрос unseen кормит и окно при входе,
// и колокольчик. Окно и колокольчик живут в разных компонентах кабинета, поэтому
// состояние общее, а запрос дедуплицируется.
export const unseenState = Vue.observable({
	loaded: false,
	count: 0,
	items: [],
	popup: null,
})

// Обновления, которые пользователь уже закрыл в этой вкладке. Страхует от повторного
// показа окна, пока seen ещё в полёте или если он не дошёл: после перезагрузки
// страницы unseen может снова вернуть то же обновление.
const DISMISSED_KEY = 'system_updates_dismissed'

function readDismissed() {
	try {
		const list = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]')
		return Array.isArray(list) ? list : []
	} catch (e) {
		return []
	}
}

function rememberDismissed(ids) {
	try {
		const list = Array.from(new Set(readDismissed().concat(ids))).slice(-100)
		sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(list))
	} catch (e) {
		// приватный режим и т.п. — без подстраховки, только серверная отметка
	}
}

let pending = null
// Токен, под которым загружены данные: сменился пользователь — грузим заново
let loadedToken = null
let pendingToken = null
// Номер запроса: ответ устаревшего запроса (например, после смены языка) не должен
// перетирать свежий.
let requestSeq = 0

export function loadUnseen({ force = false } = {}) {
	const token = localStorage.getItem('access_token')
	if (!token) return Promise.resolve()
	if (!force) {
		// Окно и колокольчик монтируются одновременно — второй вызов ждёт первый запрос
		if (pending && pendingToken === token) return pending
		if (unseenState.loaded && loadedToken === token) return Promise.resolve()
	}

	const seq = ++requestSeq
	pendingToken = token
	pending = apiSecond
		.get('v1/system-updates/unseen')
		.then(({ data }) => {
			if (seq !== requestSeq || !data) return

			const dismissed = readDismissed()
			const items = Array.isArray(data.items)
				? data.items.filter((item) => item && !dismissed.includes(item.id))
				: []
			const hidden = (Array.isArray(data.items) ? data.items.length : 0) - items.length

			unseenState.items = items
			unseenState.count = Math.max(0, (Number(data.count) || 0) - hidden)
			unseenState.popup =
				data.popup && data.popup.id && !dismissed.includes(data.popup.id) ? data.popup : null
			unseenState.loaded = true
			loadedToken = token
		})
		.catch(() => {
			// Окно и колокольчик — не критичный функционал: без данных просто молчим
		})
		.finally(() => {
			if (seq === requestSeq) pending = null
		})

	return pending
}

function dropLocally(ids) {
	const fresh = ids.filter(
		(id) =>
			unseenState.items.some((item) => item.id === id) ||
			(unseenState.popup && unseenState.popup.id === id),
	)
	unseenState.items = unseenState.items.filter((item) => !ids.includes(item.id))
	unseenState.count = Math.max(0, unseenState.count - fresh.length)
	if (unseenState.popup && ids.includes(unseenState.popup.id)) unseenState.popup = null
}

// Сразу убираем локально — интерфейс не ждёт ответа; повторный seen бэк не считает ошибкой
export function markSeen(ids) {
	const list = (Array.isArray(ids) ? ids : [ids]).filter((id) => Number.isInteger(id))
	if (!list.length) return Promise.resolve()

	rememberDismissed(list)
	dropLocally(list)

	return apiSecond.post('v1/system-updates/seen', { ids: list }).catch(() => {})
}

export function markAllSeen() {
	rememberDismissed(unseenState.items.map((item) => item.id))
	unseenState.items = []
	unseenState.count = 0
	unseenState.popup = null

	return apiSecond.post('v1/system-updates/seen', { all: true }).catch(() => {})
}

// Выход из кабинета: следующий пользователь в этой вкладке получит свои данные
export function resetUnseen() {
	requestSeq++
	pending = null
	pendingToken = null
	loadedToken = null
	unseenState.loaded = false
	unseenState.count = 0
	unseenState.items = []
	unseenState.popup = null
	try {
		sessionStorage.removeItem(DISMISSED_KEY)
	} catch (e) {
		// ignore
	}
}
